<template>
  <div
    class="content-pod-wrapper"
    :class="[
      `content-pod--${content.column_width}-width`,
      { 'is-product': displayAsProduct },
    ]"
  >
    <div
      class="content-pod"
      :class="`content-pod--${content.column_width}-width`"
      :style="content?.css_variables"
    >
      <component
        :is="contentPodURL ? 'a' : 'div'"
        class="content-pod__media-wrapper"
        :href="contentPodURL"
        :aria-label="contentPodLabel"
        @click="handleLinkClick"
      >
        <media-element
          class="content-pod__media"
          :image="content?.image"
          :image-desktop="content?.image_desktop"
          :video="content?.video"
          :video-desktop="content?.video_desktop"
          :video-external-url="content?.video_external_url"
          :video-external-desktop-url="content?.video_external_desktop_url"
          :autoplay-video="content?.video_autoplay"
        />
      </component>

      <component
        :is="contentPodURL ? 'a' : 'div'"
        v-if="showOverlay"
        class="content-pod__overlay-content content-align"
        :href="contentPodURL"
        :aria-label="contentPodLabel"
        @click="handleLinkClick"
      >
        <div
          v-if="groupContent"
          class="content-pod__content-group"
          :class="content.text_align"
        >
          <heading
            v-if="content.heading_text"
            class="content-pod__heading"
            :type="content.heading_type"
            :text="content.heading_text"
          />

          <btn
            v-if="content.button_url && content.button_text"
            class="content-pod__button"
            :class="content.button_type"
            :label="content.button_text"
          />
        </div>

        <heading
          v-if="content.heading_text && !groupContent"
          class="content-pod__heading"
          :style="getContentAlignment('heading')"
          :type="content.heading_type"
          :text="content.heading_text"
        />

        <btn
          v-if="!groupContent && content.button_url && content.button_text"
          class="content-pod__button"
          :class="content.button_type"
          :label="content.button_text"
          :style="getContentAlignment('button')"
        />
      </component>
    </div>

    <header
      v-if="displayAsProduct"
      class="content-pod__header prod-card__header"
    >
      <div class="prod-card__details">
        <a
          ref="productLink"
          class="prod-card__link"
          :href="content.product_object?.url"
          @click="handleLinkClick"
        >
          <h3
            class="prod-card__title"
            v-text="content.product_object?.title"
          />
        </a>

        <product-prices
          class="prod-card__prices"
          :currency-code="currencyCode"
          :product="content.product_object"
        />
      </div>
    </header>
  </div>
</template>

<script>

/**
 * Component: Content pod
 * ------------------------------------------------------------------------------
 * - Displays a content banner within a product grid.
 * - Note that the content object described below may contain additional
 *   properties, due to the Liquid snippet including all of the block settings.
 *   The list below contains all properties used by this Vue component.
 *
 * @param {Object} [content] - Content data object.
 * @param {Number} [content.product_index] - Index of the product card that the
 *   content pod should be placed ahead of.
 * @param {String} [content.column_width] - Identifies how many columns the
 *   content pod should occupy within the product grid.
 * @param {Object} [content.image] - Formatted image object.
 * @param {Object} [content.image_desktop] - Formatted image object for desktop.
 * @param {Object} [content.video] - Formatted video object.
 * @param {Object} [content.video_desktop] - Formatted video object for desktop.
 * @param {String} [content.video_external_url] - An external video URL
 * @param {String} [content.video_external_desktop_url] - An external video URL (desktop)
 * @param {Boolean} [content.video_autoplay] - Toggles video autoplay.
 * @param {Boolean} [content.video_loop] - Toggles video looping.
 * @param {String} [content.heading_text] - Heading element text.
 * @param {String} [content.heading_type] - Heading element type.
 * @param {String} [content.button_url] - Button element URL.
 * @param {String} [content.button_text] - Button element text.
 * @param {String} [content.button_type] - Button element type.
 * @param {Boolean} [content.content_is_grouped] - Toggles grouping of heading
 *   and button elements within the content area.
 * @param {String} [content.css_variables] - String of CSS variables for the
 *   content area and the button and heading elements.
 * @param {String} [content.block_id] - Shopify ID for the section block.
 * @param {String} [content.enable_product_display] - Enables the content pod to be displayed as a regular product (title, price, etc).
 * @param {String} [content.product_object] - Formatted (limited) product object for the current content pod with.
 */
import { mapState } from 'pinia'
import { useScreenStore } from '~stores/screen'
import { useLocaleStore } from '~stores/locale'

import Btn from '~components/btn/btn'
import Heading from '~components/heading/heading'
import MediaElement from '~components/media-element/media-element'
import ProductPrices from '~components/product-prices/product-prices'

export default {
  name: 'ContentPod',

  components: {
    Btn,
    Heading,
    MediaElement,
    ProductPrices,
  },

  props: {
    content: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['link-clicked'],

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Map locale state.
     */
    ...mapState(useLocaleStore, {
      currencyCode: 'currencyCode',
    }),

    /**
     * Whether the heading and button elements should be grouped together.
     * @returns {Boolean}
     */
    groupContent() {
      return !!this.content?.content_is_grouped
    },

    /**
     * Calculate whether the overlay content should be shown.
     * @returns {Boolean}
     */
    showOverlay() {
      const {
        button_text,
        button_url,
        heading_text,
      } = this.content

      return Boolean(heading_text || (button_url && button_text))
    },

    /**
     * Calculate whether to display content pod as a product.
     * @returns {Boolean}
     */
    displayAsProduct() {
      return Boolean(this.content.enable_product_display && this.content.product_object)
    },

    /**
     * Calculate if the whole `content-pod` media should be clickable.
     * @returns {String|Boolean}
     */
    contentPodURL() {
      return (
        this.content.button_url ||
        (this.displayAsProduct && this.content.product_object.url) ||
        false
      )
    },

    /**
     * Calculate `aria-label` text for content-pod when it's a link.
     * @returns {String|Boolean}
     */
    contentPodLabel() {
      return (
        (this.displayAsProduct && this.content.product_object.title) ||
        this.content.heading_text ||
        (this.content.button_url && this.content.button_text) ||
        this.contentPodURL
      )
    },
  },

  methods: {

    /**
     * Get the text alignment for a button or heading element based on the
     * content's horizontal alignment.
     * @param {String} elementType - Element type, either 'button' or 'heading'
     */
    getContentAlignment(elementType) {
      let propertyName = `${elementType}_align_horizontal`

      if (!this.isMobile) {
        propertyName += '_desktop'
      }

      const property = this.content[propertyName]

      if (!property) {
        return ''
      }

      /**
       * Translate the horizontal alignment property into a text alignment
       * property.
       */
      const propertyToAlignment = {
        start: 'left',
        center: 'center',
        end: 'right',
      }

      const alignment = propertyToAlignment?.[property]

      return `text-align: ${alignment}`
    },

    /**
     * Handle events for clicking the link.
     * - Used to trigger 'Product Card Click' interaction events.
     * @param {Event} event - `Click` event.
     * @emits `link-clicked`
     */
    handleLinkClick(event) {

      /**
       * If the product is not added or the click is directed to the video control
       * button, then we should not reproduce the event on the entire card
       */
      if (!this.displayAsProduct || event.target.closest('.video-element__button')) {
        return
      }

      /**
       * Send click event to the parent component.
       * - Can be used for tracking events and such.
       * - Also used to preserve user scroll position on PLP 'Back' button.
       */
      this.$emit('link-clicked', {
        handle: this.content.product_object?.handle,
        product: this.content.product_object,
      })
    },
  },
}
</script>

<style src="./content-pod.scss" lang="scss"></style>
